import React from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { Home, Camera, LogOut, Wallet, LayoutGrid, LogIn, UserCog, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useAccounts } from "../lib/accounts";
import { LOGO_URL } from "../lib/brand";
import TourOverlay from "./TourOverlay";

// Routes where the floating Snap-a-receipt FAB should be hidden — it would
// visually cover editable rows on data-entry pages.
const HIDE_FAB_ROUTES = new Set([
  "/transactions/bulk-add",
  "/snap",
]);

export default function Layout({ children }) {
  const { user, logout, endImpersonation } = useAuth();
  const { accounts, selectedId, select } = useAccounts();
  const nav = useNavigate();
  const location = useLocation();
  const hideFab = HIDE_FAB_ROUTES.has(location.pathname);
  const imp = user?.impersonation;
  const returnToAdmin = async () => {
    try {
      const me = await endImpersonation();
      if (!me) {
        toast.error("No admin session found — please log in again");
        return;
      }
      toast.success("Returned to admin");
      nav("/admin");
    } catch (e) {
      toast.error("Could not return to admin");
    }
  };

  const link = (to, Icon, label, testid) => (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 rounded-lg transition ${
          isActive ? "text-[#1a1a1a]" : "text-[#4a4a4a]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <div
            className={`p-1 rounded-lg ${isActive ? "bg-[#d9d4f1] border-2 border-[#1a1a1a]" : ""}`}
          >
            <Icon size={18} strokeWidth={2.75} />
          </div>
          <span className="text-[9px] font-bold uppercase tracking-wider">{label}</span>
        </>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-shell">
      {/* Persistent impersonation banner — always at top so admin can bail out
          without hunting through the UI. Red so it's obvious this is not the
          admin's own session. */}
      {imp && (
        <div
          data-testid="impersonation-banner"
          className="w-full bg-[#c81e1e] text-white border-b-2 border-[#1a1a1a] px-3 py-2 flex items-center gap-2 sticky top-0 z-50"
        >
          <UserCog size={16} strokeWidth={3} className="shrink-0" />
          <div className="flex-1 min-w-0 text-xs sm:text-sm font-bold truncate">
            Viewing as <span className="underline">{user?.email}</span> — you are {imp.impersonator_email}
          </div>
          <button
            data-testid="return-to-admin-btn"
            onClick={returnToAdmin}
            className="shrink-0 bg-white text-[#c81e1e] font-black uppercase tracking-wider text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded border-2 border-[#1a1a1a] hover:bg-[#fdecc8] transition"
          >
            Return to admin
          </button>
        </div>
      )}

      {/* Full-width logo banner */}
      <div className="w-full bg-[#1a1a1a] border-b-2 border-[#1a1a1a] relative">
        <img
          src={LOGO_URL}
          alt="SyncBook"
          className="w-full h-28 sm:h-36 object-contain object-center bg-[#1a1a1a]"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="font-display font-black text-3xl sm:text-4xl text-[#b5e4ca] tracking-widest opacity-0 group-[.logo-fallback]:opacity-100">
            SYNCBOOK
          </span>
        </div>
        <button
          data-testid="logout-btn"
          onClick={() => {
            logout();
            nav("/login");
          }}
          className="absolute top-3 right-3 nb-btn nb-btn-secondary !py-2 !px-3"
          title="Log out"
        >
          <LogOut size={16} strokeWidth={2.75} />
        </button>
        <div className="absolute bottom-2 left-3 flex items-center gap-2">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#b5e4ca] bg-[#1a1a1a]/70 px-2 py-1 rounded border border-[#b5e4ca]/40">
            hey {user?.name?.split(" ")[0]}
          </div>
          {user?.plan && (
            <div
              className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded border-2"
              style={{
                background: user.plan === "paid" ? "#fdecc8" : "#fff",
                borderColor: "#1a1a1a",
                color: "#1a1a1a",
              }}
              data-testid="plan-badge"
            >
              {user.plan === "paid" ? (user.trial_active ? "TRIAL" : "PRO") : "FREE"}
            </div>
          )}
          {user && user.plan !== "paid" && (
            <Link
              to="/upgrade"
              data-testid="upgrade-link"
              className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded border-2 border-[#1a1a1a] bg-[#b5e4ca]"
            >
              GO PRO
            </Link>
          )}
          {user && (
            <Link
              to="/profile"
              data-testid="profile-link"
              className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded border-2 border-[#1a1a1a] bg-white"
              title="Profile & SMS setup"
            >
              PROFILE
            </Link>
          )}
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="admin-link"
              className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded border-2 border-[#1a1a1a] bg-[#d9d4f1]"
            >
              ADMIN
            </Link>
          )}
        </div>
      </div>

      {/* Trial-ended banner (auto-downgrade + upgrade CTA) */}
      {user?.trial_ended && user?.plan !== "paid" && (
        <div
          className="max-w-2xl mx-auto w-full px-4 pt-3"
          data-testid="trial-ended-banner-shell"
        >
          <div
            className="nb-card p-3 flex items-center gap-3"
            style={{ background: "#f4c2c2" }}
          >
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-sm">Your Pro trial ended</div>
              <div className="text-[11px] font-bold">Forecast & Multi-Account are locked — your data is safe.</div>
            </div>
            <Link
              to="/upgrade"
              data-testid="trial-ended-upgrade-cta"
              className="nb-btn nb-btn-dark !py-2 !px-3 !text-xs whitespace-nowrap"
            >
              Upgrade
            </Link>
          </div>
        </div>
      )}

      {/* Trial-active banner — features a countdown ring so the days-left
          value has an at-a-glance visual weight instead of buried in text. */}
      {user?.trial_active && (
        <div
          className="max-w-2xl mx-auto w-full px-4 pt-3"
          data-testid="trial-active-banner-shell"
        >
          <div
            className="nb-card p-3 flex items-center gap-3"
            style={{ background: "#fdecc8" }}
          >
            <TrialCountdownRing daysLeft={user.trial_days_left ?? 0} />
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-sm">
                {user.trial_days_left ?? 0} day{user.trial_days_left === 1 ? "" : "s"} of Pro left
              </div>
              <div className="text-[11px] font-bold">Lock it in before your trial ends.</div>
            </div>
            <Link
              to="/upgrade"
              data-testid="trial-active-upgrade-cta"
              className="nb-btn nb-btn-dark !py-2 !px-3 !text-xs whitespace-nowrap"
            >
              See plans
            </Link>
          </div>
        </div>
      )}

      {/* Account switcher */}
      {accounts.length > 0 && (
        <div className="max-w-2xl mx-auto w-full px-4 pt-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar items-center" data-testid="account-switcher">
            {accounts.map((a) => (
              <button
                key={a.id}
                data-testid={`switch-account-${a.name}`}
                onClick={() => select(a.id)}
                className={`chip whitespace-nowrap flex items-center gap-1.5 ${selectedId === a.id ? "active" : ""}`}
                style={selectedId === a.id ? { background: a.color } : {}}
              >
                <Wallet size={12} strokeWidth={3} />
                {a.name}
              </button>
            ))}
            <Link
              to="/accounts"
              data-testid="manage-accounts-link"
              className="chip whitespace-nowrap"
            >
              + Manage
            </Link>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 pb-32 pt-4">{children}</main>

      {/* Floating action button */}
      {!hideFab && (
        <button
          data-testid="fab-snap"
          onClick={() => nav("/snap")}
          className="fixed z-40 bottom-24 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-[#1a1a1a] text-[#b5e4ca] border-2 border-[#1a1a1a] shadow-[4px_4px_0_0_#b5e4ca] flex items-center justify-center active:translate-x-1 active:translate-y-1 active:shadow-none transition"
          aria-label="Snap a receipt"
        >
          <Camera size={26} strokeWidth={2.75} />
        </button>
      )}

      {/* Bottom nav — simplified to 3 pillars: Home, [+ FAB], More.
          Everything else (History, Match with bank, Bills, Forecast, Import,
          Budgets, Goals, Duplicates, Profile, Help) lives inside the /more
          app-drawer grid. Keeps thumb-reach targets big and cognitive load low. */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-[#f4f0eb] border-t-2 border-[#1a1a1a]">
        <div className="max-w-2xl mx-auto grid grid-cols-3 items-end py-2 px-4 gap-2">
          {link("/", Home, "Home", "nav-home")}
          <div /> {/* space for FAB (centered on the middle column) */}
          {link("/more", LayoutGrid, "More", "nav-more")}
        </div>
      </nav>

      {/* Onboarding walkthrough — renders nothing when not running */}
      <TourOverlay />
    </div>
  );
}

// Free-tier trial length — kept in sync with backend TRIAL_DAYS. Used to compute
// how much of the countdown ring is filled.
const TRIAL_TOTAL_DAYS = 7;

function TrialCountdownRing({ daysLeft }) {
  // Clamp so the ring never over/underflows visually
  const remaining = Math.max(0, Math.min(TRIAL_TOTAL_DAYS, Number(daysLeft) || 0));
  const pct = remaining / TRIAL_TOTAL_DAYS;
  const size = 44;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dashOffset = c * (1 - pct);
  // Color by urgency
  const color =
    remaining <= 1 ? "#c81e1e" : remaining <= 3 ? "#c48b00" : "#12854a";
  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      data-testid="trial-countdown-ring"
      aria-label={`${remaining} of ${TRIAL_TOTAL_DAYS} trial days left`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#1a1a1a"
          strokeOpacity="0.12"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          style={{ transition: "stroke-dashoffset 400ms ease-out" }}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center font-display font-black text-sm tabular-nums"
        style={{ color }}
      >
        {remaining}
      </div>
    </div>
  );
}
